import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Spacer,
  Text,
  Table,
  TableContainer,
  Tbody,
  Th,
  Thead,
  Td,
  Tr,
  useToast,
  Skeleton,
  HStack,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';

import {
  GetOrganizationAssignmentStrategiesDocumentGQLType,
  useDeleteAssignmentStrategyMutation,
  useGetOrganizationAssignmentStrategiesQuery,
  useUpdateAssignmentStrategiesDefaultMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { AssignmentSkeletonLoadingTable } from '../AssignmentSkeletonLoadingTables';
import { DeleteAssignmentStrategyButton } from './DeleteAssignmentStrategyButton';
import { colors } from 'packages/common-frontend/colors';
import { successToastConfig } from 'packages/common-frontend/constants';
import { useLatestAssignmentPipelineRunMetadataForLpGroupingContext } from 'packages/loanProduct/hooks/LatestAssignmentPipelineRunMetadataForLpGroupingProvider';
import { useLoanProductCreditFacilityGroupContext } from 'packages/loanProduct/hooks/LoanProductCreditFacilityGroupingProvider';
import { usePreviewGenerationStatus } from 'packages/loanProduct/hooks/useGetPreviewGenerationStatus';
import { CheckLatestPipelineRunStatus } from 'packages/loanProduct/ui/v2/CheckLatestPipelineRunStatus';
import { FiChevronRight } from 'react-icons/fi';

export const AssignmentSelectStrategyTable = ({
  organizationId,
  editable,
}: {
  organizationId: string;
  editable: boolean;
}) => {
  const toast = useToast();
  const history = useHistory();

  const {
    data,
    loading: organizationAssignmentStrategiesLoading,
  } = useGetOrganizationAssignmentStrategiesQuery({
    variables: { organizationId },
    fetchPolicy: 'network-only',
  });

  const [
    updateAssignmentStrategiesDefault,
  ] = useUpdateAssignmentStrategiesDefaultMutation({
    refetchQueries: [
      {
        query: GetOrganizationAssignmentStrategiesDocumentGQLType,
        variables: { organizationId },
      },
    ],
    onCompleted: ({ updateAssignmentStrategiesDefault: response }) => {
      const { id, name, default: isDefault } = response;
      successToastConfig.id = _.uniqueId(
        `${id}-updateAssignmentStrategiesDefault-toast`,
      );
      successToastConfig.title = `Successfully ${
        isDefault ? 'set' : 'removed'
      } ${name} as the active assignment strategy`;
      toast(successToastConfig);
    },
  });

  const [
    deleteAssignmentStrategy,
    { loading: deleteAssignmentStrategyLoading },
  ] = useDeleteAssignmentStrategyMutation({
    refetchQueries: [
      {
        query: GetOrganizationAssignmentStrategiesDocumentGQLType,
        variables: { organizationId },
      },
    ],
    onCompleted: ({ deleteAssignmentStrategy: res }) => {
      const { id, name } = res;
      successToastConfig.id = _.uniqueId(
        `${id}-deleteAssignmentStrategy-toast`,
      );
      successToastConfig.title = `Successfully deleted the ${name} assignment strategy`;
      toast(successToastConfig);
    },
  });

  return (
    <Box>
      {organizationAssignmentStrategiesLoading ? (
        <AssignmentSkeletonLoadingTable numberOfRows={5} numberOfColumns={4} />
      ) : (
        <TableContainer borderWidth="1px">
          <Table>
            <Thead>
              <Tr>
                {editable && <Th width="5%">Set Default</Th>}
                <Th width="30%">Assignment Strategy Name</Th>
                <Th width="15%">Updated At</Th>
                <Th width="15%">Updated By</Th>
                <Th width="30%">Description</Th>
                <Th width="5%">{editable ? 'Actions' : ''}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data?.organization.assignmentStrategies?.length === 0 ? (
                <Tr>
                  <Td>
                    <Text color="gray">
                      A strategy has not been created yet.
                    </Text>
                  </Td>
                </Tr>
              ) : (
                _.map(
                  data?.organization.assignmentStrategies,
                  ({
                    id,
                    name,
                    default: isDefault,
                    description,
                    updatedByEmail,
                    updated_at: updatedAt,
                  }) => {
                    return (
                      <Tr
                        key={id}
                        onClick={() => {
                          if (!editable) {
                            history.push(`/assignments/strategy/${id}/apply`);
                          }
                        }}
                        _hover={{ cursor: editable ? 'auto' : 'pointer' }}
                      >
                        {editable && (
                          <Td
                            textAlign="center"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            _hover={{ cursor: 'auto' }}
                          >
                            <Checkbox
                              onChange={() => {
                                updateAssignmentStrategiesDefault({
                                  variables: {
                                    assignmentStrategyId: id,
                                    isDefault: !isDefault,
                                  },
                                });
                              }}
                              isChecked={isDefault}
                            />
                          </Td>
                        )}
                        <>
                          <Td>{name}</Td>
                          <Td>
                            {commonDate.dateToHumanDateString(
                              new Date(updatedAt),
                            )}
                          </Td>
                          <Td>{updatedByEmail}</Td>
                          <Td>{description}</Td>
                          <Td>
                            {editable ? (
                              <HStack spacing="24px">
                                <Button
                                  variant="link"
                                  color={colors.info}
                                  onClick={() =>
                                    history.push(
                                      `/settings/assignment-strategy/${id}/edit`,
                                    )
                                  }
                                >
                                  Edit
                                </Button>
                                <DeleteAssignmentStrategyButton
                                  deleteAssignmentStrategy={async () => {
                                    await deleteAssignmentStrategy({
                                      variables: { assignmentStrategyId: id },
                                    });
                                  }}
                                  isLoading={deleteAssignmentStrategyLoading}
                                />
                              </HStack>
                            ) : (
                              <FiChevronRight color="#137CBD" />
                            )}
                          </Td>
                        </>
                      </Tr>
                    );
                  },
                )
              )}
            </Tbody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export const AssignmentSelectStrategyContainer = ({
  organizationId,
}: {
  organizationId: string;
}) => {
  const { lpToCfEntityGrouping } = useLoanProductCreditFacilityGroupContext();
  const {
    metadata,
  } = useLatestAssignmentPipelineRunMetadataForLpGroupingContext();
  const {
    isCompleted,
    latestJob,
    StatusAlert: PreviewStatusAlert,
  } = usePreviewGenerationStatus({
    loanProductIds:
      lpToCfEntityGrouping?.loanProducts.map(({ id }) => id) ?? [],
    organizationId,
    reportingDate: metadata?.reportingDate,
    fileVersion: metadata?.fileVersion,
  });

  if (!metadata) {
    return <Skeleton w="100%" h="100%" />;
  }

  return (
    <CheckLatestPipelineRunStatus>
      {({ StatusAlert }) => {
        if (metadata === null || isCompleted === null) {
          return (
            <Box>
              <Flex width="1250px" direction="column">
                <Flex justify="space-between" mb="24px">
                  <Heading as="h2">Select Strategy</Heading>
                  <Spacer />
                </Flex>
                <Skeleton w="100%" h="50%" />
              </Flex>
            </Box>
          );
        }
        if (
          metadata.status !== 'success' ||
          (metadata.status === 'success' && !isCompleted)
        ) {
          return (
            <Box>
              <Flex width="1250px" direction="column">
                <Flex justify="space-between" mb="24px">
                  <Heading as="h2">Select Strategy</Heading>
                  <Spacer />
                </Flex>
                <StatusAlert
                  status={
                    !isCompleted
                      ? 'inProgress'
                      : metadata?.status || 'inProgress'
                  }
                />
                <Skeleton w="100%" h="50%" />
              </Flex>
            </Box>
          );
        }

        return (
          <Box>
            <Flex width="1250px" direction="column">
              <Flex justify="space-between" mb="24px">
                <Heading as="h2">Select Strategy</Heading>
                <Spacer />
              </Flex>
              <Text fontSize="lg" mb="32px" color="gray">
                Select a strategy to apply; you&apos;ll have the opportunity to
                review it before running it.
              </Text>
            </Flex>
            {latestJob &&
              latestJob.status === 'error' &&
              latestJob.assignmentMethod === 'strategy' && (
                <PreviewStatusAlert />
              )}
            <AssignmentSelectStrategyTable
              organizationId={organizationId}
              editable={false}
            />
          </Box>
        );
      }}
    </CheckLatestPipelineRunStatus>
  );
};
